// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// Selection is global and survives a view change: pinning a station in the Ledger and then
// opening Your Clock should show that station, not reset to a default. All of it lives in the
// URL hash so any state a reader reaches can be linked to, and the durable preferences (their
// station, their pollutant) are mirrored to localStorage so a return visit lands where they left.

export interface AppState {
  view: string;
  station: string | null;
  episode: string | null;
  pollutant: 'pm2.5' | 'pm10';
  year: number | null;
}

const STORAGE_KEY = 'air-episodes.prefs';
const listeners = new Set<(state: AppState) => void>();

let state: AppState = {
  view: 'average',
  station: null,
  episode: null,
  pollutant: 'pm2.5',
  year: null,
};

function readStored(): Partial<AppState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<AppState>) : {};
  } catch {
    return {};
  }
}

function persist(): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ station: state.station, pollutant: state.pollutant })
    );
  } catch {
    /* private browsing, a full quota — a lost preference is not worth an error state */
  }
}

function parseHash(): Partial<AppState> {
  const hash = location.hash.replace(/^#\/?/, '');
  if (!hash) return {};
  const [view, query] = hash.split('?');
  const out: Partial<AppState> = {};
  if (view) out.view = view;
  const params = new URLSearchParams(query ?? '');
  if (params.has('station')) out.station = params.get('station');
  if (params.has('episode')) out.episode = params.get('episode');
  const pollutant = params.get('p');
  if (pollutant === 'pm2.5' || pollutant === 'pm10') out.pollutant = pollutant;
  const year = params.get('year');
  if (year && /^\d{4}$/.test(year)) out.year = Number(year);
  return out;
}

function writeHash(): void {
  const params = new URLSearchParams();
  if (state.station) params.set('station', state.station);
  if (state.episode) params.set('episode', state.episode);
  if (state.pollutant !== 'pm2.5') params.set('p', state.pollutant);
  if (state.year) params.set('year', String(state.year));
  const query = params.toString();
  const next = `#/${state.view}${query ? `?${query}` : ''}`;
  if (location.hash !== next) history.replaceState(null, '', next);
}

export function getState(): AppState {
  return state;
}

export function setState(patch: Partial<AppState>, options: { silent?: boolean } = {}): void {
  const before = JSON.stringify(state);
  state = { ...state, ...patch };
  if (JSON.stringify(state) === before) return;
  writeHash();
  persist();
  if (!options.silent) for (const listener of listeners) listener(state);
}

export function subscribe(listener: (state: AppState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called once at boot: hash wins over storage, storage wins over the defaults. */
export function initState(defaults: Partial<AppState>): void {
  state = { ...state, ...defaults, ...readStored(), ...parseHash() };
  writeHash();
  window.addEventListener('hashchange', () => {
    const fromHash = parseHash();
    state = { ...state, ...fromHash };
    for (const listener of listeners) listener(state);
  });
}
